'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useCallback } from 'react';
import { Track } from 'livekit-client';
import { BarVisualizer, useRemoteParticipants, useRoomContext } from '@livekit/components-react';
import { ChatTextIcon } from '@phosphor-icons/react/dist/ssr';
import { ChatInput } from '@/components/livekit/chat/chat-input';
import { DeviceSelect } from '@/components/livekit/device-select';
import { TrackToggle } from '@/components/livekit/track-toggle';
import { Toggle } from '@/components/ui/toggle';
import { muteAllAudio } from '@/helpers/commonFunctions';
import { useAgentControlBar } from '@/hooks/use-agent-control-bar';
import { cn } from '@/lib/utils';
/**
 * A control bar specifically designed for voice assistant interfaces
 */
export function ActionBar(_a) {
    var { controls, saveUserChoices = true, capabilities, className, onSendMessage, onChatOpenChange, onDeviceError, setChat, chat, setMic, flag, setFlag, setClose, close, setMicOn, micOn, showInactivity } = _a, props = __rest(_a, ["controls", "saveUserChoices", "capabilities", "className", "onSendMessage", "onChatOpenChange", "onDeviceError", "setChat", "chat", "setMic", "flag", "setFlag", "setClose", "close", "setMicOn", "micOn", "showInactivity"]);
    const participants = useRemoteParticipants();
    const [chatOpen, setChatOpen] = React.useState(false);
    const [voiceFlagInput, setVoiceFlagInput] = React.useState(false);
    const room = useRoomContext();
    React.useEffect(() => {
        setVoiceFlagInput(flag === 'request_name' || flag === 'request_email' || flag === 'request_phone' ? true : false);
    }, [flag]);
    // Sync external chat state -> local chatOpen + mic toggle logic
    React.useEffect(() => {
        if (chat === undefined)
            return;
        setChatOpen(chat);
        const chatOnly = typeof window !== 'undefined' && sessionStorage.getItem('chat') === 'true' && chat;
        if (chatOnly) {
            microphoneToggle.toggle(false);
            muteAllAudio(true);
            return;
        }
        if (chat && ['request_name', 'request_email', 'request_phone'].includes(flag)) {
            microphoneToggle.toggle(false);
            muteAllAudio(false);
        }
        else if (chat) {
            microphoneToggle.toggle(false);
            muteAllAudio(true);
        }
        else if (micOn) {
            microphoneToggle.toggle(false);
        }
        else {
            microphoneToggle.toggle(true);
            muteAllAudio(false);
        }
    }, [chat, flag]);
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setMicOn(sessionStorage.getItem('micMute') === 'true' && true);
        }, 500); // 1 second delay
        return () => clearTimeout(timer); // cleanup
    }, []);
    React.useEffect(() => {
        const chatOnly = typeof window !== 'undefined' && sessionStorage.getItem('chat') === 'true';
        if (chatOnly) {
            microphoneToggle.toggle(false);
            return;
        }
        if (micOn && (room === null || room === void 0 ? void 0 : room.state) === 'connected') {
            microphoneToggle.toggle(false);
        }
        else {
            microphoneToggle.toggle(true);
        }
    }, [micOn, room === null || room === void 0 ? void 0 : room.state]);
    React.useEffect(() => {
        if (showInactivity) {
            microphoneToggle.toggle(false);
        }
        else if (sessionStorage.getItem('micMute') === 'true' &&
            sessionStorage.getItem('chat') === 'false') {
            microphoneToggle.toggle(false);
        }
        else if (sessionStorage.getItem('chat') === 'true') {
            microphoneToggle.toggle(false);
        }
        else {
            microphoneToggle.toggle(true);
        }
    }, [showInactivity]);
    // React.useEffect(() => {
    //   microphoneToggle.toggle(sessionStorage.getItem('micMute') === 'false' ? true : false);
    // }, [micOn]);
    React.useEffect(() => {
        if (close) {
            microphoneToggle.toggle(false); // mic OFF when chat open
        }
    }, [close]);
    const [isSendingMessage, setIsSendingMessage] = React.useState(false);
    const isAgentAvailable = participants.some((p) => p.isAgent);
    const isInputDisabled = !chatOpen || !isAgentAvailable || isSendingMessage;
    const { micTrackRef, visibleControls, cameraToggle, microphoneToggle, screenShareToggle, handleAudioDeviceChange, handleVideoDeviceChange, } = useAgentControlBar({
        controls,
        saveUserChoices,
    });
    React.useEffect(() => {
        setMic(micTrackRef);
    }, [micTrackRef]);
    const handleSendMessage = async (message) => {
        setIsSendingMessage(true);
        try {
            await (onSendMessage === null || onSendMessage === void 0 ? void 0 : onSendMessage(message));
        }
        finally {
            setIsSendingMessage(false);
        }
    };
    React.useEffect(() => {
        onChatOpenChange === null || onChatOpenChange === void 0 ? void 0 : onChatOpenChange(chatOpen);
    }, [chatOpen, onChatOpenChange]);
    const onMicrophoneDeviceSelectError = useCallback((error) => {
        onDeviceError === null || onDeviceError === void 0 ? void 0 : onDeviceError({ source: Track.Source.Microphone, error });
    }, [onDeviceError]);
    const onCameraDeviceSelectError = useCallback((error) => {
        onDeviceError === null || onDeviceError === void 0 ? void 0 : onDeviceError({ source: Track.Source.Camera, error });
    }, [onDeviceError]);
    React.useEffect(() => {
        if (sessionStorage.getItem('chat') === 'true') {
            setChatOpen(true);
        }
        else {
            setChatOpen(false);
        }
        // mic ON when chat closed
    }, []);
    // Force mic OFF on first load
    return (_jsxs("div", Object.assign({ "aria-label": "Voice assistant controls", className: cn('relative z-20 mx-2 mb-2 flex flex-col rounded-[24px] bg-transparent p-1', className) }, props, { children: [capabilities.supportsChatInput && (_jsx("div", { 
                // inert={!chatOpen}
                className: cn('relative overflow-hidden transition-[height] duration-300 ease-out', chatOpen || voiceFlagInput ? 'h-[46px]' : 'h-0'), children: _jsx("div", { className: cn('dark:border-separator1 absolute inset-x-0 top-0 flex h-9 w-full transition-opacity duration-150 ease-linear', chatOpen || voiceFlagInput ? 'opacity-100 delay-150' : 'opacity-0'), children: _jsx(ChatInput, { onSend: handleSendMessage, disabled: isInputDisabled, className: "w-full border-white", uiFlag: flag, setFlag: setFlag }) }) })), _jsxs("div", { className: "flex hidden flex-row justify-between gap-1", children: [_jsxs("div", { className: "flex gap-1", children: [visibleControls.microphone && (_jsxs("div", { className: "flex items-center gap-0", children: [_jsx(TrackToggle, { variant: "primary", source: Track.Source.Microphone, pressed: microphoneToggle.enabled, disabled: microphoneToggle.pending, onPressedChange: microphoneToggle.toggle, className: "peer/track group/track relative w-auto pr-3 pl-3 has-[+_*]:rounded-r-none has-[+_*]:border-r-0 has-[+_*]:pr-2", children: _jsx(BarVisualizer, { barCount: 3, trackRef: micTrackRef, options: { minHeight: 5 }, className: "absolute right-0 flex h-full w-auto items-center justify-center gap-0.5", children: _jsx("span", { className: cn([
                                                    'h-full w-0.5 origin-center rounded-2xl',
                                                    'group-data-[state=off]/track:bg-destructive-foreground group-data-[state=on]/track:bg-white',
                                                    'data-lk-muted:bg-muted',
                                                ]) }) }) }), _jsx(DeviceSelect, { size: "sm", kind: "audioinput", requestPermissions: false, onMediaDeviceError: onMicrophoneDeviceSelectError, onActiveDeviceChange: handleAudioDeviceChange, className: cn([
                                            'pl-2',
                                            'peer-data-[state=off]/track:text-destructive-foreground',
                                            'hover:text-fg1 focus:text-fg1',
                                            'hover:peer-data-[state=off]/track:text-destructive-foreground focus:peer-data-[state=off]/track:text-destructive-foreground',
                                            'hidden rounded-l-none md:block',
                                        ]) })] })), capabilities.supportsVideoInput && visibleControls.camera && (_jsxs("div", { className: "flex items-center gap-0", children: [_jsx(TrackToggle, { variant: "primary", source: Track.Source.Camera, pressed: cameraToggle.enabled, pending: cameraToggle.pending, disabled: cameraToggle.pending, onPressedChange: cameraToggle.toggle, className: "peer/track relative w-auto pr-3 pl-3 disabled:opacity-100 has-[+_*]:rounded-r-none has-[+_*]:border-r-0 has-[+_*]:pr-2" }), _jsx(DeviceSelect, { size: "sm", kind: "videoinput", requestPermissions: false, onMediaDeviceError: onCameraDeviceSelectError, onActiveDeviceChange: handleVideoDeviceChange, className: cn([
                                            'pl-2',
                                            'peer-data-[state=off]/track:text-destructive-foreground',
                                            'hover:text-fg1 focus:text-fg1',
                                            'hover:peer-data-[state=off]/track:text-destructive-foreground focus:peer-data-[state=off]/track:text-destructive-foreground',
                                            'rounded-l-none',
                                        ]) })] }))] }), _jsxs("div", { className: "flex gap-1", children: [capabilities.supportsScreenShare && visibleControls.screenShare && (_jsx("div", { className: "flex items-center gap-0", children: _jsx(TrackToggle, { variant: "secondary", source: Track.Source.ScreenShare, pressed: screenShareToggle.enabled, disabled: screenShareToggle.pending, onPressedChange: screenShareToggle.toggle, className: "relative w-auto" }) })), visibleControls.chat && (_jsx(Toggle, { variant: "secondary", "aria-label": "Toggle chat", pressed: chatOpen, onPressedChange: setChatOpen, disabled: !isAgentAvailable, className: "aspect-square h-full", children: _jsx(ChatTextIcon, { weight: "bold" }) }))] })] })] })));
}
